import PropTypes from 'prop-types';
import {useRef, useState} from 'react';
import {connect} from 'react-redux';
import MeatBallMenu from '../../../svg/icons/meatballMenu.svg';
import Address from '../../classes/Address';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import useHandleClickOutside from '../../hooks/useHandleClickOutside';
import {showOverlay} from '../../v2/actions/overlay-actions';
import doMetric from '../../v2/tracking/bigQueryMetricRecorder';
import Track from '../../v2/utils/tracking';
import useStyles from './MeatballMenu.styles';
import Translation from '../../v2/lang/Translation';
import {isMobile} from '../../v2/selectors/DeviceSelectors';
import MenuItem from './MenuItem';
import AddressPropType from '../../propTypes/Address.proptype';

export function MeatballMenu(props) {
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const [displayMenuContent, setDisplayMenuContent] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const address = new Address(props.address);
  
  useHandleClickOutside(menuRef, () => setDisplayMenuContent(false), buttonRef);
  
  const classes = useStyles({...props, displayMenuContent});
  
  useDidUpdateEffect(() => {
    if (!displayMenuContent) {
      setActiveIndex(0);
    }
  }, [displayMenuContent]);
  
  const handleClick = () => {
    if (props.isMobile) {
      props.showOverlay(props.overlayId, props.overlayDetails);
    } else {
      setDisplayMenuContent(!displayMenuContent);
    }
    
    if (!displayMenuContent && props.onClick) {
      props.onClick();
    }
  
    try {
      doMetric('addressOptionsButton', [{name: 'event', value: 'click'}]);
    } catch (error) {
      Track.track(error);
    }
  };
  
  const selectMenuItem = (overlayId) => {
    if (!props.isMobile) {
      props.showOverlay(overlayId, props.overlayDetails);
      setDisplayMenuContent(false);
    }
  };
  
  const preventDefaults = (event) => {
    const stopPropagation = () => {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    };
    
    const keyHandlers = {
      ArrowUp: stopPropagation,
      ArrowDown: stopPropagation,
      Escape: stopPropagation,
      Enter: stopPropagation,
    };
    
    if (keyHandlers[event.key]) {
      keyHandlers[event.key]();
    }
  };
  
  const onKeyDown = (event) => {
    if (!displayMenuContent) return;
    
    const keyHandlers = {
      ArrowUp: () => {
        if (activeIndex < 1) {
          setActiveIndex(props.listItems.length - 1);
        } else {
          setActiveIndex(activeIndex - 1);
        }
      },
      ArrowDown: () => {
        if (activeIndex === props.listItems.length - 1) {
          setActiveIndex(0);
        } else {
          setActiveIndex(activeIndex + 1);
        }
      },
      Escape: () => {
        if (!props.isMobile) {
          setDisplayMenuContent(false);
        }
      },
      Tab: () => {
        if (!props.isMobile) {
          setDisplayMenuContent(false);
        }
      },
      Enter: () => {
        if (!props.isMobile) {
          selectMenuItem(props.listItems[activeIndex].overlayId);
        }
      },
    };
    
    if (keyHandlers[event.key]) {
      preventDefaults(event);
      keyHandlers[event.key]();
    }
  };
  
  return (
    <>
      <button
        className={classes.meatballMenu}
        id={`MeatballMenu--${props.id}`}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        aria-label={`${Translation.getText('checkout.delivery.meatballMenu.altText')} - ${address.getAddressString()}`}
        aria-haspopup="true"
        aria-controls={`MeatballMenu--${props.id}`}
        aria-expanded={displayMenuContent}
        type="button"
        ref={buttonRef}
        aria-live="polite"
      >
        <img className={classes.meatballMenuSvg} src={MeatBallMenu} alt="" />
      </button>
      {displayMenuContent && (
        <ul
          className={classes.menuContent}
          id={`MeatballMenu--${props.id}`}
          aria-labelledby={`MeatballMenu--${props.id}`}
          role="menu"
          tabIndex="0"
          ref={menuRef}
        >
          {props.listItems.map((item, index) => (
            <MenuItem
              item={item}
              active={index === activeIndex}
              onClick={() => selectMenuItem(item.overlayId)}
            />
          ))}
        </ul>
      )}
    </>
  );
}

const OverlayDetailsPropType = {
  addressId: PropTypes.string.isRequired,
};

const ListItemsPropType = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  overlayId: PropTypes.string.isRequired,
};

MeatballMenu.propTypes = {
  isMobile: PropTypes.bool,
  id: PropTypes.string,
  overlayId: PropTypes.string,
  onClick: PropTypes.func,
  showOverlay: PropTypes.func,
  listItems: PropTypes.arrayOf(PropTypes.shape(ListItemsPropType).isRequired).isRequired,
  overlayDetails: PropTypes.shape(OverlayDetailsPropType).isRequired,
  address: PropTypes.shape(AddressPropType).isRequired,
};

const mapStateToProps = state => ({
  isMobile: isMobile(state),
});

export default connect(mapStateToProps, {showOverlay})(MeatballMenu);
